import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ModalAcessoComponentes } from './ModalAcessoComponentes';

type AcessoComponente = {
  id: string;
  nome: string;
  acessoComponentes?: Array<{
    id: string;
    componente: string;
  }>;
  acessoSistemas?: Array<{
    id: string;
    sistema: string;
    sistemaId?: string;
  }>;
};

type Row = {
  id: string;
  nome: string;
  acessoComponentes?: Array<{
    id: string;
    componente: string;
  }>;
  acessoSistemas?: Array<{
    id: string;
    sistema: string;
    sistemaId?: string;
  }>;
};

type Props = {
  initialRows?: AcessoComponente[];
  onAcessoComponentesChange?: (acessoComponentes: AcessoComponente[]) => void;
  currentCamadaId?: string;
};

const convertToRows = (acessoComponentes: AcessoComponente[]): Row[] => {
  return acessoComponentes.map((item) => ({
    id: item.id,
    nome: item.nome,
    acessoComponentes: item.acessoComponentes || [],
    acessoSistemas: item.acessoSistemas || [],
  }));
};

export const ListaAcessoComponentes: React.FC<Props> = ({
  initialRows = [],
  onAcessoComponentesChange,
  currentCamadaId,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (row: Row) => {
    setEditingRow(row);
    setOpenModal(true);
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onAcessoComponentesChange) {
      const acessoComponentes = newRows.map((row) => ({
        id: row.id,
        nome: row.nome,
        acessoComponentes: row.acessoComponentes || [],
        acessoSistemas: row.acessoSistemas || [],
      }));
      onAcessoComponentesChange(acessoComponentes);
    }
  };

  const handleAddNew = () => {
    setEditingRow(null);
    setOpenModal(true);
  };

  const handleModalSave = (values: any) => {
    let newRows: Row[] = [];
    if (values.id) {
      // Editando item existente
      newRows = rows.map((r) =>
        r.id === values.id
          ? {
            ...r,
            nome: values.nome,
            acessoComponentes: values.acessoOutrosComponentes || [],
            acessoSistemas: values.acessoOutrosSistemas || [],
          }
          : r
      );
    } else {
      // Novo item
      const newRow: Row = {
        id: String(Date.now()),
        nome: values.nome,
        acessoComponentes: values.acessoOutrosComponentes || [],
        acessoSistemas: values.acessoOutrosSistemas || [],
      };
      newRows = [newRow, ...rows];
    }

    setRows(newRows);
    if (onAcessoComponentesChange) {
      const acessoComponentes = newRows.map((row) => ({
        id: row.id,
        nome: row.nome,
        acessoComponentes: row.acessoComponentes || [],
        acessoSistemas: row.acessoSistemas || [],
      }));
      onAcessoComponentesChange(acessoComponentes);
    }
    setOpenModal(false);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setEditingRow(null);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      <Button
        variant="contained"
        color="primary"
        sx={{ width: '80px' }}
        onClick={handleAddNew}
        startIcon={<AddIcon />}
      >
        Novo
      </Button>

      <ModalAcessoComponentes
        open={openModal}
        onClose={handleModalClose}
        onSave={handleModalSave}
        initialValues={
          editingRow
            ? {
              id: editingRow.id,
              nome: editingRow.nome,
              acessoOutrosComponentes: editingRow.acessoComponentes || [],
              acessoOutrosSistemas: editingRow.acessoSistemas || [],
            }
            : undefined
        }
      />

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ações</TableCell>
              <TableCell>Nome</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>{row.nome}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
